
import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { supabase } from "@/integrations/supabase/client";
import NavigationMenu from "@/components/layout/NavigationMenu";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { ChevronLeft, RefreshCw } from "lucide-react";
import { Link } from "react-router-dom";
import { useCourseStats } from "@/hooks/useCourseStats";
import CourseStatsFilters from "@/components/course-stats/CourseStatsFilters";
import PerformanceSummaryCards from "@/components/course-stats/PerformanceSummaryCards";
import BestPerformanceSection from "@/components/course-stats/BestPerformanceSection";
import AdvancedStatsCards from "@/components/course-stats/AdvancedStatsCards";
import ScoreEvolutionChart from "@/components/course-stats/ScoreEvolutionChart";
import PrecisionEvolutionChart from "@/components/course-stats/PrecisionEvolutionChart";
import RoundsHistoryTable from "@/components/course-stats/RoundsHistoryTable";
import ExternalAccountLink from "@/components/course-stats/ExternalAccountLink";
import { useToast } from "@/hooks/use-toast";

const CourseStats = () => {
  const navigate = useNavigate();
  const { t } = useTranslation(['course-stats', 'navigation']);
  const { toast } = useToast();
  
  const {
    stats,
    filteredStats,
    isLoading,
    accountLink,
    isAccountLinkLoading,
    filters,
    setFilters,
    courses,
    syncRounds,
    isSyncing,
    refetch
  } = useCourseStats();

  useEffect(() => {
    const checkAuth = async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        navigate("/auth");
      }
    };
    checkAuth();
  }, [navigate]);

  const handleSync = async () => {
    try {
      await syncRounds();
      toast({
        title: t('course-stats:sync.success'),
        description: t('course-stats:sync.successDescription'),
      });
    } catch (error: any) {
      toast({
        variant: "destructive",
        title: t('course-stats:sync.error'),
        description: error.message,
      });
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-golf-50 to-white">
      <NavigationMenu />
      
      <div className="container mx-auto p-4 md:p-8">
        <div className="flex justify-between items-center mb-6">
          <Link to="/" className="text-golf-600 hover:text-golf-700 flex items-center gap-2">
            <ChevronLeft className="h-5 w-5" />
            <span>{t('navigation:backToDashboard')}</span>
          </Link>
          
          {accountLink?.is_verified && (
            <Button 
              onClick={handleSync} 
              disabled={isSyncing}
              variant="outline"
              size="sm"
            >
              <RefreshCw className={`h-4 w-4 mr-2 ${isSyncing ? 'animate-spin' : ''}`} />
              {isSyncing ? t('course-stats:sync.syncing') : t('course-stats:sync.syncNow')}
            </Button>
          )}
        </div>

        <h1 className="text-3xl font-bold text-golf-800 mb-6">{t('course-stats:title')}</h1>

        {/* Account Link Section */}
        <ExternalAccountLink 
          accountLink={accountLink}
          isLoading={isAccountLinkLoading}
          onLinkSuccess={refetch}
        />

        {accountLink?.is_verified && (
          <>
            {/* Filters */}
            <CourseStatsFilters 
              filters={filters}
              setFilters={setFilters}
              courses={courses}
            />

            {isLoading ? (
              <div className="flex justify-center items-center py-12">
                <RefreshCw className="h-8 w-8 animate-spin text-golf-600" />
              </div>
            ) : filteredStats.length === 0 ? (
              <Card className="mt-6">
                <CardContent className="py-12 text-center">
                  <p className="text-muted-foreground">{t('course-stats:noData')}</p>
                  <Button onClick={handleSync} className="mt-4" disabled={isSyncing}>
                    <RefreshCw className={`h-4 w-4 mr-2 ${isSyncing ? 'animate-spin' : ''}`} />
                    {t('course-stats:sync.syncNow')}
                  </Button>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-6 mt-6">
                {/* KPI Cards */}
                <PerformanceSummaryCards stats={filteredStats} />

                {/* Best Performance & Tendencies */}
                <BestPerformanceSection stats={filteredStats} />

                {/* Advanced Stats: Missed Fairway Tendencies, Scrambling, Sand Save */}
                <AdvancedStatsCards stats={filteredStats} />

                {/* Score Evolution Chart */}
                <ScoreEvolutionChart stats={filteredStats} />

                {/* Precision Evolution Chart */}
                <PrecisionEvolutionChart stats={filteredStats} />

                {/* Rounds History Table */}
                <RoundsHistoryTable stats={filteredStats} />
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default CourseStats;
